import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Autocomplete, 
  FormControl,
  CircularProgress,
  Tooltip,
  Chip,
  OutlinedInput,
  InputLabel
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import HistoryIcon from '@mui/icons-material/History';
import { projectsApi } from '../../../services/api/projectsApi';
import { COLORS } from '../../../constants/radar';
import { PROJECT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

/**
 * Reusable project search component with autocomplete
 *
 * @param {Object} props
 * @param {Object|String|Array} props.value - Currently selected project(s) (object/string/array based on multiple and returnValue)
 * @param {Function} props.onChange - Callback when project selection changes
 * @param {Boolean} props.multiple - Whether to allow multiple selection
 * @param {String} props.label - Label for the input field
 * @param {String} props.placeholder - Placeholder text for the input field
 * @param {Number} props.width - Width of the component (default: 300px)
 * @param {Object} props.sx - Additional styles to apply to the FormControl
 * @param {Boolean} props.disabled - Whether the component is disabled
 * @param {Boolean} props.required - Whether the field is required
 * @param {String} props.size - Size of the input field (default: "small")
 * @param {String} props.employeeId - Employee ID to fetch employee-specific projects
 * @param {Boolean} props.showAssignmentIndicators - Whether to show current/previous assignment indicators
 * @param {String} props.returnValue - What to return: PROJECT_RETURN_TYPES.ID, PROJECT_RETURN_TYPES.NAME, or undefined (full object)
 * @param {Boolean} props.allowNoProjects - Whether to show "No Projects Assigned" option
 * @param {String} props.noProjectsLabel - Label for the "no projects" option
 * @param {String} props.noProjectsValue - Value for the "no projects" option
 * @param {String} props.projectPurpose - Filter by project purpose code (e.g., 'delivery_project', 'bench'). If null, shows all projects
 */
const ProjectSearch = ({
  value,
  onChange,
  multiple = false,
  label,
  placeholder = "Search for a project",
  width = 300,
  sx = {},
  disabled = false,
  required = false,
  size = "small",
  employeeId = null,
  showAssignmentIndicators = true,
  returnValue,
  allowNoProjects = false,
  // Show the project code next to the name — "Software Mind Bench (AM-5269)" — in the
  // input and the list (opt-in; the code comes from /projects-lite and /projects).
  showProjectCode = false,
  noProjectsLabel = "No Projects Assigned",
  noProjectsValue = "no-projects",
  projectPurpose = null
}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, [employeeId]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use different API endpoints based on whether employeeId is provided
      let projectsData = employeeId
        ? await projectsApi.getProjectsLite(employeeId)
        : await projectsApi.getAllProjects();

      // Filter by project purpose if provided
      if (projectPurpose && projectsData && projectsData.length > 0) {
        projectsData = projectsData.filter(project =>
          project.projectPurpose?.code === projectPurpose
        );
      }

      setProjects(projectsData || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (event, newValue) => {
    if (multiple) {
      // Handle multiple selection
      if (!newValue || newValue.length === 0) {
        onChange([]);
        return;
      }

      // Return value based on returnValue prop for multiple selection
      if (returnValue === PROJECT_RETURN_TYPES.ID) {
        onChange(newValue.map(project => project.id || project));
      } else if (returnValue === PROJECT_RETURN_TYPES.NAME) {
        onChange(newValue.map(project => project.name || project));
      } else {
        // Default behavior: return project objects with id, name, and projectPurpose
        const simplifiedProjects = newValue.map(project =>
          typeof project === 'string' ? project : {
            id: project.id,
            name: project.name,
            code: project.code || null,
            projectPurpose: project.projectPurpose || null,
          }
        );
        onChange(simplifiedProjects);
      }
    } else {
      // Handle single selection
      if (!newValue) {
        onChange(null);
        return;
      }

      // Return value based on returnValue prop
      if (returnValue === PROJECT_RETURN_TYPES.ID) {
        onChange(newValue.id);
      } else if (returnValue === PROJECT_RETURN_TYPES.NAME) {
        onChange(newValue.name);
      } else {
        // Default behavior: return project object with id, name, projectPurpose, and client
        const simplifiedProject = {
          id: newValue.id,
          name: newValue.name,
          code: newValue.code || null,
          projectPurpose: newValue.projectPurpose || null,
          client: newValue.client || null,
        };
        onChange(simplifiedProject);
      }
    }
  };

  // Convert string value to project object for display
  const getDisplayValue = () => {
    if (multiple) {
      if (!value || !Array.isArray(value) || value.length === 0) return [];
      
      return value.map(val => {
        if (typeof val === 'string') {
          // Handle special "no projects" value
          if (val === noProjectsValue) {
            return { id: noProjectsValue, name: noProjectsLabel };
          }
          // Find project by name or id
          const project = projects.find(p => p.name === val || p.id === val);
          return project || { id: val, name: val };
        }
        // Value is already an object
        return val;
      });
    } else {
      if (!value) return null;
      
      if (typeof value === 'string') {
        // Handle special "no projects" value
        if (value === noProjectsValue) {
          return { id: noProjectsValue, name: noProjectsLabel };
        }
        // Find project by name or id
        const project = projects.find(p => p.name === value || p.id === value);
        return project || null;
      }
      
      // Value is already an object
      return value;
    }
  };

  return (
    <FormControl sx={{ width: `${width}px`, ...sx }}>
      <Autocomplete
        fullWidth
        multiple={multiple}
        options={allowNoProjects ? [{ id: noProjectsValue, name: noProjectsLabel }, ...projects] : projects}
        loading={loading}
        getOptionLabel={(option) => (showProjectCode && option?.code ? `${option.name} (${option.code})` : option?.name || '')}
        value={getDisplayValue()}
        onChange={handleProjectChange}
        noOptionsText={error || "No projects found. Please check with your administrator."}
        isOptionEqualToValue={(option, value) => {
          // Handle both full objects and simplified {id, name} objects
          return option?.id === value?.id;
        }}
        disabled={disabled}
        slotProps={{
          listbox: {
            style: { maxHeight: 300 }
          },
          paper: {
            elevation: 3,
            sx: { mt: 0.5 }
          },
          ...(multiple && {
            chip: {
              size: 'small',
              sx: {
                backgroundColor: COLORS.PRIMARY,
                color: 'white',
                fontStyle: 'normal',
                height: '24px',
                fontSize: '0.75rem',
                '.MuiChip-deleteIcon': {
                  color: 'white',
                  fontSize: '16px',
                  '&:hover': { 
                    color: COLORS.SECONDARY 
                  }
                }
              }
            }
          })
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size={size}
            required={required}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563eb',
                }
              }
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          
          // Special styling for "No Projects" option
          if (option?.id === noProjectsValue) {
            return (
              <li 
                {...rest}
                key={key || option?.id}
                style={{
                  borderBottom: '1px solid #eaeaea',
                  padding: '8px 16px',
                  fontStyle: 'italic',
                  backgroundColor: '#f8fafc',
                  color: '#64748b'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  width: '100%',
                }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {option?.name || ''}
                  </Typography>
                </Box>
              </li>
            );
          }
          
          return (
            <li 
              {...rest}
              key={key || option?.id}
              style={{
                borderBottom: '1px solid #eaeaea',
                padding: '8px 16px',
                ...(showAssignmentIndicators && option?.isEmployeeAssigned ? {
                  borderLeft: '3px solid #4caf50',
                } : showAssignmentIndicators && option?.wasEmployeeAssigned ? {
                  borderLeft: '3px solid #2196f3',
                } : {})
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                width: '100%',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 500,
                    ...(showAssignmentIndicators && option?.isEmployeeAssigned ? { color: '#2e7d32' } : 
                        showAssignmentIndicators && option?.wasEmployeeAssigned ? { color: '#1565c0' } : {})
                  }}>
                    {option?.name || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {showProjectCode && option?.code && (
                      <Typography variant="caption" sx={{ color: '#64748B' }}>{option.code}</Typography>
                    )}
                    {showAssignmentIndicators && (
                      <>
                      {option?.isEmployeeAssigned && (
                        <Tooltip title="Currently assigned">
                          <CircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                      {option?.wasEmployeeAssigned && (
                        <Tooltip title="Previously assigned">
                          <HistoryIcon color="info" fontSize="small" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {option?.description?.substring(0, 50)}
                    {option?.description?.length > 50 ? '...' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {option?.projectPurpose && (
                      <Chip 
                        label={option.projectPurpose.name || ''} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          backgroundColor: showAssignmentIndicators && option?.isEmployeeAssigned ? '#e8f5e9' : 
                                          showAssignmentIndicators && option?.wasEmployeeAssigned ? '#e3f2fd' : '#e3f2fd',
                          color: '#1565c0'
                        }}
                      />
                    )}
                    {option?.businessArea && (
                      <Chip 
                        label={option?.businessArea?.name || ''} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          backgroundColor: showAssignmentIndicators && option?.isEmployeeAssigned ? '#e8f5e9' : 
                                          showAssignmentIndicators && option?.wasEmployeeAssigned ? '#e3f2fd' : '#f5f5f5'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </li>
          );
        }}
      />
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </FormControl>
  );
};

export default ProjectSearch; 